"use client";

import {
  ArrowRightLeft,
  BadgeCheck,
  ChevronsUpDown,
  Loader2,
  LogOut,
  PackageCheck,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { authRepository } from "@/repository/authRepository";
import { localAuthRepository } from "@/repository/localAuthRepository";
import { cn } from "@/lib/utils";

import { useToast } from "@/hooks/use-toast";

export function NavUser({
  user,
  isPlatformMode = false,
  canSwitchMode = false,
  canManagePlan = false,
  isSwitchingMode = false,
  onModeSwitch,
}: {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
  isPlatformMode?: boolean;
  canSwitchMode?: boolean;
  canManagePlan?: boolean;
  isSwitchingMode?: boolean;
  onModeSwitch?: () => void | Promise<void>;
}) {
  const { isMobile } = useSidebar();
  const { toast } = useToast();

  const initials = user.name
    .split(/\s+/)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleLogout = async () => {
    // サーバー側でリフレッシュトークンを失効させ httpOnly Cookie を削除する。
    // これをしないと Cookie が残り、ログアウト後もリロードで自動再ログインしてしまう。
    await authRepository.logout();
    localAuthRepository.clearAll();

    // 認証状態変化をlayout-wrapperに通知
    window.dispatchEvent(
      new CustomEvent("authStateChanged", {
        detail: { isLoggedIn: false },
      })
    );

    toast({
      title: "ログアウトしました",
    });
    // ページをリロードして認証状態を確実に更新
    window.location.href = "/";
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={
                isPlatformMode
                  ? "border border-white/15 bg-white/10 text-white shadow-sm hover:bg-white/15 hover:text-white data-[state=open]:bg-white/15 data-[state=open]:text-white"
                  : "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground font-normal"
              }
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback
                  className={cn(
                    "rounded-lg",
                    isPlatformMode && "bg-teal-50 text-teal-950"
                  )}
                >
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate font-medium">{user.name}</span>
                  {canSwitchMode ? (
                    <span
                      className={cn(
                        "shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold leading-none",
                        isPlatformMode
                          ? "bg-teal-200/20 text-teal-100"
                          : "bg-teal-50 text-teal-800"
                      )}
                    >
                      {isPlatformMode ? "運営" : "通常"}
                    </span>
                  ) : null}
                </span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-(--radius-container)"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback
                    className={cn(
                      "rounded-lg",
                      isPlatformMode && "bg-teal-50 text-teal-950"
                    )}
                  >
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {canSwitchMode ? (
              <>
                <DropdownMenuItem
                  disabled={isSwitchingMode}
                  onSelect={() => void onModeSwitch?.()}
                >
                  {isSwitchingMode ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <ArrowRightLeft className="size-4" />
                  )}
                  {isPlatformMode
                    ? "通常モードへの切り替え"
                    : "運営モードへの切り替え"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            ) : null}
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to="/settings/account" className="flex items-center">
                  <BadgeCheck className="mr-2 h-4 w-4" />
                  アカウント
                </Link>
              </DropdownMenuItem>
              {canManagePlan && !isPlatformMode ? (
                <DropdownMenuItem asChild>
                  <Link to="/billing/contract" className="flex items-center">
                    <PackageCheck className="mr-2 h-4 w-4" aria-hidden="true" />
                    契約プラン
                  </Link>
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut />
              ログアウト
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
